/**
 * 1on1 振り返り API の契約 — フロント/バック共有の単一 SoT。
 *
 * ペア（マネージャー×メンバー、共有相手を含む）と、毎週のセッション（1 回の Meet =
 * 1 conferenceRecord）の req/res Zod schema をここに集約する。
 * - API の OpenAPI schema (`apps/api/product/src/features/one-on-one/api/schemas.ts`)
 * - Web の API クライアント/フック (`apps/web/product-innovation-lab/src/hooks/useOneOnOne.ts`)
 * - pipeline の Gemini 要約出力の検証 (`@cortex/domain-one-on-one` の `parseSummary`)
 * がすべてこの 1 ファイルから import する。
 *
 * ブラウザにもバンドルされるため Firestore 等のサーバー専用依存を持ち込まない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering, HR
 * @graph-business 1on1 ペア・セッション・要約の API 契約 (Zod schema + 推論型) の単一 SoT
 * @graph-connects none
 */
import { z } from 'zod';
/** 要約の生成状態。pending=Meet 終了済みで要約待ち / generated=Gemini 生成済み / confirmed=リーダーが確認済み。@graph-connects none */
export const ONE_ON_ONE_SUMMARY_STATUSES = ['pending', 'generated', 'confirmed'];
/** @graph-connects none */
export const oneOnOneSummaryStatusSchema = z.enum(ONE_ON_ONE_SUMMARY_STATUSES);
/** ペアの状態。archived は Meet Space と購読を止めた状態。@graph-connects none */
export const ONE_ON_ONE_PAIR_STATUSES = ['active', 'archived'];
/** @graph-connects none */
export const oneOnOnePairStatusSchema = z.enum(ONE_ON_ONE_PAIR_STATUSES);
/** JST 月曜起点の週キー（YYYY-MM-DD）。@graph-connects none */
export const oneOnOneWeekOfSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** 要約 1 節あたりの上限。Gemini の冗長出力とフロントの表示崩れを抑える。@graph-connects none */
const SUMMARY_ITEM_MAX = 20;
/** @graph-connects none */
const SUMMARY_TEXT_MAX = 500;
/**
 * 1on1 で合意したアクション。`done` は本人・リーダーのどちらもトグルできる。
 * @graph-connects none
 */
export const oneOnOneAgreedActionSchema = z.object({
    text: z.string().min(1).max(SUMMARY_TEXT_MAX),
    done: z.boolean(),
    dueWeek: oneOnOneWeekOfSchema.optional(),
});
/**
 * Gemini が固定スキーマで生成し、リーダーが修正・確認する構造化要約。
 * @graph-connects none
 */
export const oneOnOneSummarySchema = z.object({
    /** 話したトピック */
    topics: z.array(z.string().min(1).max(SUMMARY_TEXT_MAX)).max(SUMMARY_ITEM_MAX),
    /** マネージャーが与えたフィードバック */
    feedbackGiven: z.array(z.string().min(1).max(SUMMARY_TEXT_MAX)).max(SUMMARY_ITEM_MAX),
    /** 本人が口にした懸念・相談 */
    memberConcerns: z.array(z.string().min(1).max(SUMMARY_TEXT_MAX)).max(SUMMARY_ITEM_MAX),
    /** 合意したアクション */
    agreedActions: z.array(oneOnOneAgreedActionSchema).max(SUMMARY_ITEM_MAX),
    /** 次回確認すること */
    nextCheck: z.array(z.string().min(1).max(SUMMARY_TEXT_MAX)).max(SUMMARY_ITEM_MAX),
});
/**
 * Meet が生成した成果物（録画 / 文字起こし）の所在。原文は Drive リンクで開く
 * （participants に Drive 権限を付与済み）。録画は Drive にだけ置き、文字起こしテキストは
 * 要約用に専用バケットへも保存するため gcsUri を持つ。
 * @graph-connects none
 */
export const oneOnOneArtifactSchema = z.object({
    driveFileId: z.string().min(1),
    webViewLink: z.string().min(1),
    gcsUri: z.string().min(1).optional(),
});
/**
 * ペアのレスポンス。spaceName / subscriptionName は内部情報なので返さない。
 * @graph-connects none
 */
export const oneOnOnePairResponseSchema = z.object({
    pairId: z.string().min(1),
    memberEmail: z.email(),
    ownerEmail: z.email(),
    /**
     * ペアのリーダー（ownerEmail）の Firestore `users.nickName`。1 メンバーが複数のリーダーと
     * ペアを持つので、画面でどのリーダーとのペアかを示すのに使う。引けなければ省略
     */
    ownerNickName: z.string().min(1).optional(),
    /** memberEmail を必ず含む。共有相手（サブリーダー等）もここに入る */
    participants: z.array(z.email()).min(1),
    meetingUri: z.string().min(1),
    status: oneOnOnePairStatusSchema,
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/**
 * ペア取得（GET /one-on-one/pairs）の応答。
 *
 * 1 人のメンバーは複数のリーダー（上長・リーダー候補など）とそれぞれペアを持つ。
 * - `pairs`: そのメンバーの active なペアのうち、**自分が participants に入っているものだけ**
 *   （admin でも当事者でなければ出さない。他のリーダーのペアの存在は漏らさない）。
 *   自分がリーダーのペアを先頭に、残りは作成が古い順
 * - `canOpen`: 自分がリーダーとしてこのメンバーのペアをこの場で開設できるか
 *   （`canOpenPairFor` かつ、自分がリーダーの active なペアがまだ無い）
 * @graph-connects none
 */
export const oneOnOnePairLookupResponseSchema = z.object({
    pairs: z.array(oneOnOnePairResponseSchema),
    canOpen: z.boolean(),
});
/**
 * 自分が participants に入っているペアの一覧（GET /one-on-one/participating-pairs）の応答。
 * 過去の 1on1 を振り返る入口なので archived も含め、新しい順に並べる。
 * @graph-connects none
 */
export const oneOnOneParticipatingPairsResponseSchema = z.object({
    pairs: z.array(oneOnOnePairResponseSchema),
});
/**
 * リーダー向けフィードバックの生成状態。
 *
 * `skipped` は「1on1 として振り返るには短い回」等で意図的に作らなかったもので、失敗ではない。
 * @graph-connects none
 */
export const oneOnOneLeaderFeedbackStatusSchema = z.enum([
    'pending',
    'generated',
    'skipped',
    'failed',
]);
/**
 * 機械的に数えた発言バランス。LLM の推測ではなく文字起こしの集計値。
 *
 * **話者の割り当てが欠けた回には入らない**（片方が 0 文字になる形。氏名の表記ゆれで実際に
 * 起きた）。数えられなかった回に誤った比率を見せるより、持たせない方を選んでいる。
 * @graph-connects none
 */
export const oneOnOneSpeakingBalanceSchema = z.object({
    leaderChars: z.number().int().nonnegative(),
    memberChars: z.number().int().nonnegative(),
    leaderTurns: z.number().int().nonnegative(),
    memberTurns: z.number().int().nonnegative(),
    /** リーダーの発言量が全体に占める割合（%）。教科書 3-6 の目安は 20% */
    leaderSharePercent: z.number().int().min(0).max(100),
    durationSeconds: z.number().int().nonnegative().nullable(),
});
/** 判断基準 1 つから見た観察。@graph-connects none */
export const oneOnOneLeaderObservationSchema = z.object({
    /** Dev-MMBC 3-6 の framework key */
    frameworkKey: z.string().min(1),
    /** 教科書での基準名。生成時に key から引いて持たせる（画面が教科書を引かずに済む） */
    frameworkName: z.string().optional(),
    observation: z.string().min(1),
    basis: z.string().min(1),
});
/**
 * 1on1 の進め方についてリーダーに返す材料。点数も合否も持たない。
 *
 * メンバーには返さない（リーダー本人とその上長だけが読む）。
 * @graph-connects none
 */
export const oneOnOneLeaderFeedbackSchema = z.object({
    observations: z.array(oneOnOneLeaderObservationSchema).max(SUMMARY_ITEM_MAX),
    suggestions: z.array(z.string().min(1)).max(SUMMARY_ITEM_MAX),
    notEnoughEvidence: z.array(z.string().min(1)).max(SUMMARY_ITEM_MAX),
    speakingBalance: oneOnOneSpeakingBalanceSchema.optional(),
    /** 参照した教科書の章（現在は 3-6 のみ） */
    chapterId: z.string().min(1),
});
/** @graph-connects none */
export const ONE_ON_ONE_LEADER_NOTE_MAX = 2000;
/** 発言バランスの目安（リーダー：メンバー = 2：8）。教科書 3-6 が定める値。@graph-connects none */
export const ONE_ON_ONE_LEADER_SHARE_TARGET_PERCENT = 20;
/**
 * セッション（1 回の 1on1）のレスポンス。`privateNotes` と `leaderFeedback` 系は
 * participants のうちリーダー以上にだけ返し、本人には返さない（API 側で除去する）。
 * @graph-connects none
 */
export const oneOnOneSessionResponseSchema = z.object({
    sessionId: z.string().min(1),
    weekOf: oneOnOneWeekOfSchema,
    startedAt: z.iso.datetime(),
    endedAt: z.iso.datetime().optional(),
    attendees: z.array(z.email()),
    recording: oneOnOneArtifactSchema.optional(),
    transcript: oneOnOneArtifactSchema.optional(),
    summary: oneOnOneSummarySchema.optional(),
    summaryStatus: oneOnOneSummaryStatusSchema,
    summarizedAt: z.iso.datetime().optional(),
    privateNotes: z.string().optional(),
    leaderFeedback: oneOnOneLeaderFeedbackSchema.optional(),
    leaderFeedbackStatus: oneOnOneLeaderFeedbackStatusSchema.optional(),
    /** 作らなかった理由（skipped / failed のとき） */
    leaderFeedbackReason: z.string().optional(),
    /** リーダーが書き添える、この回の背景 */
    leaderNote: z.string().optional(),
    updatedAt: z.iso.datetime(),
});
/** タイムラインは新しい週から返し、`nextBefore` で続きを取る。@graph-connects none */
export const oneOnOneSessionListResponseSchema = z.object({
    sessions: z.array(oneOnOneSessionResponseSchema),
    /** 次ページ取得用の startedAt（ISO）。無ければ null */
    nextBefore: z.iso.datetime().nullable(),
});
/** 共有相手の上限。3 人 1on1（サブリーダー同席）が主用途なので十分。@graph-connects none */
export const ONE_ON_ONE_MAX_PARTICIPANTS = 10;
/**
 * ペア開設リクエスト。開設者自身と memberEmail は API 側で participants に補完する。
 * @graph-connects none
 */
export const createOneOnOnePairRequestSchema = z.object({
    memberEmail: z.email(),
    /** 追加で共有する相手（サブリーダー等） */
    participants: z.array(z.email()).max(ONE_ON_ONE_MAX_PARTICIPANTS).default([]),
});
/**
 * 共有相手の更新（全置換）。memberEmail を含まない配列は API 側で拒否する。
 * @graph-connects none
 */
export const updateOneOnOneParticipantsRequestSchema = z.object({
    participants: z.array(z.email()).min(1).max(ONE_ON_ONE_MAX_PARTICIPANTS),
});
/** 合意アクションの完了トグル。summary 全体を送らずに index 指定で更新する。@graph-connects none */
export const oneOnOneAgreedActionUpdateSchema = z.object({
    index: z.number().int().min(0),
    done: z.boolean(),
});
/** @graph-connects none */
export const ONE_ON_ONE_PRIVATE_NOTES_MAX = 4000;
/**
 * セッション更新リクエスト。本人（リーダー権限のない participant）は
 * `agreedActionUpdates` のみ許可し、それ以外のフィールドは API 側で 403 にする。
 * @graph-connects none
 */
export const updateOneOnOneSessionRequestSchema = z
    .object({
    agreedActionUpdates: z.array(oneOnOneAgreedActionUpdateSchema).max(SUMMARY_ITEM_MAX),
    summary: oneOnOneSummarySchema,
    /** 確認済みにする。pending / generated へ戻す操作は提供しない */
    summaryStatus: z.literal('confirmed'),
    privateNotes: z.string().max(ONE_ON_ONE_PRIVATE_NOTES_MAX),
    leaderNote: z.string().max(ONE_ON_ONE_LEADER_NOTE_MAX),
})
    .partial()
    .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: '更新するフィールドを 1 つ以上指定してください',
});
// ============================================================
// 四半期目標（評価ツール spreadsheet から取り込む）
// ============================================================
/**
 * 会計四半期のラベル（評価ツールのタブ名と同じ表記。例: `2027年1Q`）。
 * 期首は 7 月で、年は年度末の暦年（2026-07〜2027-06 が 2027 年度）
 * @graph-connects none
 */
export const oneOnOneFiscalQuarterLabelSchema = z.string().regex(/^\d{4}年[1-4]Q$/);
/** 四半期 doc の ID（例: `2027-1Q`）。ラベルの `年` を `-` にした ASCII 表記。@graph-connects none */
export const oneOnOneFiscalQuarterIdSchema = z.string().regex(/^\d{4}-[1-4]Q$/);
/** @graph-connects none */
const GOAL_TEXT_MAX = 8_000;
/**
 * 目標 1 件に対する評価（自己 / リーダー / グループのいずれか）。
 * 達成度は比率（100% = 1）、評価点は設定比率 × 達成度 × 乗数のシート計算値
 * @graph-connects none
 */
export const oneOnOneGoalEvaluationSchema = z.object({
    achievement: z.number().optional(),
    score: z.number().optional(),
});
/**
 * 四半期目標 1 件（評価ツールの No.1〜5 の 1 行）
 * @graph-connects none
 */
export const oneOnOneGoalSchema = z.object({
    no: z.number().int().min(1),
    /** 目標本文（100% の達成ライン・加点要素を含むセル全文） */
    text: z.string().min(1).max(GOAL_TEXT_MAX),
    /** 難易度（Average / Difficult 等の表記そのまま） */
    difficulty: z.string().optional(),
    /** 乗数 */
    multiplier: z.number().optional(),
    /** 設定比率（比率。40% = 0.4） */
    weight: z.number().optional(),
    /** 振り返りコメント */
    reflection: z.string().max(GOAL_TEXT_MAX).optional(),
    self: oneOnOneGoalEvaluationSchema,
    leader: oneOnOneGoalEvaluationSchema,
    group: oneOnOneGoalEvaluationSchema,
});
/**
 * 四半期タブ 1 枚分（ヘッダ + 目標一覧）
 * @graph-connects none
 */
export const oneOnOneGoalQuarterSchema = z.object({
    fiscalQuarter: oneOnOneFiscalQuarterLabelSchema,
    quarterId: oneOnOneFiscalQuarterIdSchema,
    employeeNo: z.string().optional(),
    /** シート上の名前表記（ニックネーム。大文字小文字は揺れる） */
    name: z.string().optional(),
    currentGrade: z.string().optional(),
    evaluatedGrade: z.string().optional(),
    /** 四半期の総合評価点（自己 / リーダー / グループ） */
    scores: z.object({
        self: z.number().optional(),
        leader: z.number().optional(),
        group: z.number().optional(),
    }),
    goals: z.array(oneOnOneGoalSchema).max(10),
    /** 設定比率の合計（通常 1） */
    totalWeight: z.number().optional(),
});
//# sourceMappingURL=one-on-one.js.map